import * as React from "react";
import { cn } from "../../lib/utils";

// ==================== Number Cell ====================

interface NumberCellProps {
  value: number;
  currency?: string;
  subtitle?: string;
  className?: string;
}

export function NumberCell({
  value,
  currency,
  subtitle,
  className,
}: NumberCellProps) {
  return (
    <div className={cn("flex flex-col", className)}>
      <div className="text-body-medium-medium text-surface-ink-neutral-normal">
        {currency}{" "}
        {value.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
      {subtitle && (
        <div className="text-body-small-regular text-surface-ink-neutral-muted">
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ==================== Icon Cell ====================

interface IconCellProps {
  icon: React.ReactNode;
  background?: "neutral" | "primary" | "success" | "warning" | "danger";
  className?: string;
}

export function IconCell({
  icon,
  background = "neutral",
  className,
}: IconCellProps) {
  const backgrounds = {
    neutral: "bg-surface-fill-neutral-faded",
    primary: "bg-action-fill-primary-faded",
    success: "bg-action-fill-positive-faded",
    warning: "bg-action-fill-warning-faded",
    danger: "bg-action-fill-negative-faded",
  };

  return (
    <div
      className={cn(
        "inline-flex items-center justify-center w-10 h-10 rounded-medium",
        backgrounds[background],
        className
      )}
    >
      {icon}
    </div>
  );
}

// ==================== Spacer Cell ====================

export function SpacerCell() {
  return <div className="w-full h-full" />;
}

// ==================== Slot Cell ====================

interface SlotCellProps {
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  className?: string;
}

export function SlotCell({ onDragStart, onDragEnd, className }: SlotCellProps) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        "flex items-center justify-center cursor-grab active:cursor-grabbing",
        className
      )}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        className="text-surface-ink-neutral-muted"
      >
        <path
          d="M6 4C6 3.44772 5.55228 3 5 3C4.44772 3 4 3.44772 4 4C4 4.55228 4.44772 5 5 5C5.55228 5 6 4.55228 6 4Z"
          fill="currentColor"
        />
        <path
          d="M6 8C6 7.44772 5.55228 7 5 7C4.44772 7 4 7.44772 4 8C4 8.55228 4.44772 9 5 9C5.55228 9 6 8.55228 6 8Z"
          fill="currentColor"
        />
        <path
          d="M6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13C5.55228 13 6 12.5523 6 12Z"
          fill="currentColor"
        />
        <path
          d="M12 4C12 3.44772 11.5523 3 11 3C10.4477 3 10 3.44772 10 4C10 4.55228 10.4477 5 11 5C11.5523 5 12 4.55228 12 4Z"
          fill="currentColor"
        />
        <path
          d="M12 8C12 7.44772 11.5523 7 11 7C10.4477 7 10 7.44772 10 8C10 8.55228 10.4477 9 11 9C11.5523 9 12 8.55228 12 8Z"
          fill="currentColor"
        />
        <path
          d="M12 12C12 11.4477 11.5523 11 11 11C10.4477 11 10 11.4477 10 12C10 12.5523 10.4477 13 11 13C11.5523 13 12 12.5523 12 12Z"
          fill="currentColor"
        />
      </svg>
    </div>
  );
}

// ==================== Avatar Cell ====================

interface AvatarCellProps {
  name: string;
  initials?: string;
  avatar?: string;
  subtitle?: string;
  color?: string;
  className?: string;
}

export function AvatarCell({
  name,
  initials,
  avatar,
  subtitle,
  color = "a1",
  className,
}: AvatarCellProps) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {avatar ? (
        <img
          src={avatar}
          alt={name}
          className="w-10 h-10 rounded-full object-cover"
        />
      ) : (
        <div
          className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center text-body-medium-medium",
            `bg-${color}`
          )}
        >
          {initials || name.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex flex-col">
        <div className="text-body-medium-medium text-surface-ink-neutral-normal">
          {name}
        </div>
        {subtitle && (
          <div className="text-body-small-regular text-surface-ink-neutral-muted">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}
